import { cn } from '@/lib/utils';
import { VariantImage } from '@/components/variant-image';
import { videoPosterUrl } from '@flowmuse/api-core/media-shared';
import { parseWxH } from '@flowmuse/models/shared/image-size';
import { resolveAssetRefPreview } from '@flowmuse/models/shared/asset-ref';
import { useI18n } from '@/providers/i18n';
import { useMemo } from 'react';
import { FileIcon, Image as ImageIcon, Music, Video as VideoIcon, Images } from 'lucide-react';

const SUMMARY_THUMB_TRANSFORM = { width: 128, height: 128, resize: 'cover', quality: 80 } as const;

type UploadCollapsedSummaryProps = {
  data: Record<string, any> | undefined;
  className?: string;
};

const inferNameFromUrl = (url: string): string | undefined => {
  try {
    const u = new URL(String(url), typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const parts = u.pathname.split('/').filter(Boolean);
    const last = parts[parts.length - 1];
    if (!last) return undefined;
    return decodeURIComponent(last);
  } catch {
    try {
      const s = String(url);
      const last = s.split('?')[0]?.split('#')[0]?.split('/').filter(Boolean).pop();
      return last ? decodeURIComponent(last) : undefined;
    } catch {
      return undefined;
    }
  }
};

const inferSuffixFromName = (name: string): string | undefined => {
  try {
    const base = String(name).trim();
    if (!base) return undefined;
    const lastDot = base.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === base.length - 1) return undefined;
    const ext = base.slice(lastDot + 1).trim().toLowerCase();
    return ext || undefined;
  } catch {
    return undefined;
  }
};

const inferExtFromMime = (mime: string): string | undefined => {
  try {
    const m = String(mime).toLowerCase().trim();
    if (!m) return undefined;
    if (m === 'image/jpeg') return 'jpg';
    if (m === 'image/jpg') return 'jpg';
    if (m === 'image/png') return 'png';
    if (m === 'image/webp') return 'webp';
    if (m === 'image/gif') return 'gif';
    if (m === 'video/mp4') return 'mp4';
    if (m === 'video/quicktime') return 'mov';
    if (m === 'audio/mpeg') return 'mp3';
    if (m === 'audio/mp3') return 'mp3';
    if (m === 'audio/wav') return 'wav';
    if (m === 'audio/x-wav') return 'wav';
    if (m === 'audio/ogg') return 'ogg';
    if (m === 'audio/webm') return 'webm';
    if (m === 'application/pdf') return 'pdf';
    // generic: use subtype when safe
    const slash = m.indexOf('/');
    if (slash > 0 && slash < m.length - 1) {
      const subtype = m.slice(slash + 1);
      // strip vendor/tree prefixes
      const cleaned = subtype.replace(/^x-/, '').replace(/^vnd\./, '').replace(/^x\./, '');
      if (/^[a-z0-9.+-]{1,16}$/.test(cleaned)) return cleaned;
    }
  } catch {}
  return undefined;
};


const simplifyRatioFromWH = (w: number, h: number): string | undefined => {
  try {
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return undefined;
    const wi = Math.round(w);
    const hi = Math.round(h);
    const gcd = (a: number, b: number): number => {
      let x = Math.abs(a);
      let y = Math.abs(b);
      while (y) {
        const t = x % y;
        x = y;
        y = t;
      }
      return x || 1;
    };
    const g = gcd(wi, hi);
    return `${Math.max(1, Math.floor(wi / g))}:${Math.max(1, Math.floor(hi / g))}`;
  } catch {
    return undefined;
  }
};

const COMMON_ASPECT_RATIOS = new Set<string>([
  '1:1',
  '2:3',
  '3:2',
  '3:4',
  '4:3',
  '4:5',
  '5:4',
  '16:9',
  '9:16',
  '21:9',
  '9:21',
]);

const formatAspectRatioNxN = (ratio: string): string => {
  try {
    const raw = String(ratio).trim();
    if (!raw) return raw;
    // normalize separators to 'x'
    if (/^\d+(\.\d+)?\s*[:/x]\s*\d+(\.\d+)?$/i.test(raw)) {
      return raw.replace(/\s*[:/x]\s*/i, 'x');
    }
    return raw;
  } catch {
    return String(ratio);
  }
};

const fmtSec = (n: number) => (Number.isInteger(n) ? `${n}s` : `${Math.round(n * 10) / 10}s`);

const formatMaybeSeconds = (value: unknown): string | undefined => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return fmtSec(value);
  }
  if (typeof value === 'string' && value.trim()) {
    const raw = value.trim();
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return fmtSec(n);
    return raw;
  }
  return undefined;
};

export const UploadCollapsedSummary = ({ data, className }: UploadCollapsedSummaryProps) => {
  const { t } = useI18n();
  const d = (data ?? {}) as any;

  const fileType = (typeof d?.fileType === 'string' && d.fileType) || undefined;
  const contentUrl = typeof d?.content?.url === 'string' ? String(d.content.url) : undefined;
  const contentName = typeof d?.content?.name === 'string' ? String(d.content.name) : undefined;
  const contentMime = typeof d?.content?.type === 'string' ? String(d.content.type) : undefined;

  const name = contentName || (typeof d?.sourceUrl === 'string' ? inferNameFromUrl(d.sourceUrl) : undefined) || (contentUrl ? inferNameFromUrl(contentUrl) : undefined);
  const displayType = (() => {
    const byName = name ? inferSuffixFromName(name) : undefined;
    if (byName) return byName;
    const byMime = contentMime ? inferExtFromMime(contentMime) : undefined;
    if (byMime) return byMime;
    return undefined;
  })();

  const isUploaded = Boolean(contentUrl);
  const autoText = t('Auto');

  // 真实分辨率统一存于 size（"WxH"）；兼容旧数据的 width/height（与历史上 aspectRatio 写成 "WxH" 的情况）。
  const videoWH =
    fileType === 'video'
      ? (parseWxH((d as any)?.size) ??
         (() => {
           const w = Number((d as any)?.width);
           const h = Number((d as any)?.height);
           return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0 ? { width: w, height: h } : undefined;
         })() ??
         parseWxH((d as any)?.aspectRatio))
      : undefined;

  const videoAspectRatio =
    videoWH ? (simplifyRatioFromWH(videoWH.width, videoWH.height) ?? undefined) :
    (fileType === 'video' && typeof d?.aspectRatio === 'string' && /^\d+:\d+$/.test(d.aspectRatio.trim()))
      ? String(d.aspectRatio).trim()
      : undefined;

  const videoResolution = videoWH
    ? `${Math.round(videoWH.width)}x${Math.round(videoWH.height)}`
    : undefined;

  const videoDuration = fileType === 'video'
    ? (formatMaybeSeconds(d?.durationSeconds) ?? formatMaybeSeconds(d?.duration) ?? undefined)
    : undefined;

  const audioDuration = fileType === 'audio'
    ? (formatMaybeSeconds(d?.durationSeconds) ?? formatMaybeSeconds(d?.duration) ?? undefined)
    : undefined;

  const imageWH =
    fileType === 'image'
      ? (parseWxH((d as any)?.size) ??
         (() => {
           const w = Number((d as any)?.width);
           const h = Number((d as any)?.height);
           return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0 ? { width: w, height: h } : undefined;
         })())
      : undefined;

  const imageResolution = imageWH
    ? `${Math.round(imageWH.width)}x${Math.round(imageWH.height)}`
    : undefined;

  const imageAspectRatio = (() => {
    if (fileType !== 'image') return undefined;
    if (!imageWH) return '-';
    const r = simplifyRatioFromWH(imageWH.width, imageWH.height);
    return r && COMMON_ASPECT_RATIOS.has(r) ? r : '-';
  })();

  const headerLabel = (() => {
    if (fileType === 'image') return t('Uploaded image');
    if (fileType === 'video') return t('Uploaded video');
    if (fileType === 'audio') return t('Uploaded audio');
    if (fileType === 'file') return t('Uploaded file');
    return t('Upload');
  })();

  const icon = (() => {
    if (fileType === 'image') return <ImageIcon size={32} className="text-muted-foreground" />;
    if (fileType === 'video') return <VideoIcon size={32} className="text-muted-foreground" />;
    if (fileType === 'audio') return <Music size={32} className="text-muted-foreground" />;
    return <FileIcon size={32} className="text-muted-foreground" />;
  })();

  const assetPreviewUrl = typeof d?.assetPreviewUrl === 'string' ? String(d.assetPreviewUrl) : undefined;

  // 缩略图的源地址：图片为原图，视频为首帧 poster；展示时优先 media 服务预生成的缩略图。
  const thumbSource = useMemo(() => {
    const effectiveUrl = resolveAssetRefPreview(contentUrl, assetPreviewUrl);
    if (!effectiveUrl) return undefined;
    if (fileType === 'image') return effectiveUrl;
    if (fileType === 'video') return videoPosterUrl(effectiveUrl, 'first') || undefined;
    return undefined;
  }, [contentUrl, assetPreviewUrl, fileType]);

  const subjectImagesCount = (() => {
    try {
      const arr = (d as any)?.subjectImages;
      if (!Array.isArray(arr)) return 0;
      return arr.filter((u: unknown) => typeof u === 'string' && String(u).trim().length > 0).length;
    } catch {
      return 0;
    }
  })();
  const isSubjectGroup = fileType === 'image' && subjectImagesCount > 1;

  return (
    <div className={cn('flex h-full w-full items-start gap-4 p-3', className)}>
      <div className="relative h-32 w-32 shrink-0 overflow-hidden rounded-2xl border border-sidebar-border/30 bg-sidebar-accent/30 flex items-center justify-center shadow-none">
        <VariantImage
          url={thumbSource ?? ''}
          transform={SUMMARY_THUMB_TRANSFORM}
          alt={headerLabel}
          className="h-full w-full object-cover transition-transform duration-500 hover:scale-110"
          loading="lazy"
          decoding="async"
          fallback={<div className="text-muted-foreground/40">{icon}</div>}
        />
        {isSubjectGroup ? (
          <div className="pointer-events-none absolute top-1.5 right-1.5 inline-flex items-center gap-1 rounded-md border border-white/15 bg-black/55 px-1.5 py-0.5 text-[10px] font-bold tracking-wider leading-none text-white shadow-sm ring-1 ring-white/10 backdrop-blur-sm">
            <Images size={10} />
            <span>×{subjectImagesCount}</span>
          </div>
        ) : null}
      </div>

      <div className="min-w-0 flex-1 flex flex-col h-32">
        <div className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/30 mb-1">{headerLabel}</div>
        <div className="text-sm font-bold truncate text-foreground/90">{name ?? '-'}</div>

        <div className="mt-2 flex flex-1 flex-col text-[11px] leading-snug">
          {/* 中间参数：固定高度容器（占据第 2~4 行） */}
          <div className="grid grid-rows-3 gap-1">
            {fileType === 'video' ? (
              <>
                <div className="truncate">
                  <span className="text-muted-foreground/60">{t('Aspect ratio')}:</span>{' '}
                  <span className="text-foreground/80 font-medium">
                    {isUploaded ? (videoAspectRatio ? formatAspectRatioNxN(videoAspectRatio) : autoText) : '-'}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground/60">{t('Resolution')}:</span>
                  <span className="text-foreground/80 font-mono tabular-nums text-[10px]">
                    {isUploaded ? (videoResolution ?? autoText) : '-'}
                  </span>
                </div>
                <div className="truncate">
                  <span className="text-muted-foreground/60">{t('Duration')}:</span>{' '}
                  <span className="text-foreground/80 font-medium">{isUploaded ? (videoDuration ?? autoText) : '-'}</span>
                </div>
              </>
            ) : fileType === 'image' ? (
              <>
                <div className="truncate">
                  <span className="text-muted-foreground/60">{t('Aspect ratio')}:</span>{' '}
                  <span className="text-foreground/80 font-medium">
                    {isUploaded ? (imageAspectRatio === '-' ? '-' : formatAspectRatioNxN(String(imageAspectRatio))) : '-'}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground/60">{t('Resolution')}:</span>
                  <span className="text-foreground/80 font-mono tabular-nums text-[10px]">
                    {isUploaded ? (imageResolution ?? autoText) : '-'}
                  </span>
                </div>
                <div className="truncate">
                  <span className="text-muted-foreground/60">{t('Type')}:</span>{' '}
                  <span className="text-foreground/80 font-medium">{isUploaded ? (displayType ?? '-') : '-'}</span>
                </div>
              </>
            ) : fileType === 'audio' ? (
              <>
                <div className="truncate">
                  <span className="text-muted-foreground/60">{t('Duration')}:</span>{' '}
                  <span className="text-foreground/80 font-medium">{isUploaded ? (audioDuration ?? autoText) : '-'}</span>
                </div>
                <div className="truncate">
                  <span className="text-muted-foreground/60">{t('Type')}:</span>{' '}
                  <span className="text-foreground/80 font-medium">{isUploaded ? (displayType ?? '-') : '-'}</span>
                </div>
                <div className="truncate opacity-0 select-none" aria-hidden="true">
                  &nbsp;
                </div>
              </>
            ) : (
              <>
                <div className="truncate">
                  <span className="text-muted-foreground/60">{t('Type')}:</span>{' '}
                  <span className="text-foreground/80 font-medium">{displayType ?? fileType ?? '-'}</span>
                </div>
                <div className="truncate opacity-0 select-none" aria-hidden="true">
                  &nbsp;
                </div>
                <div className="truncate opacity-0 select-none" aria-hidden="true">
                  &nbsp;
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


